import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Disclosure } from '@headlessui/react';
import { ChartPieIcon, HeartIcon, ChevronDoubleLeftIcon, ChevronDoubleRightIcon, ChevronRightIcon, ChevronUpIcon, UserIcon, MagnifyingGlassIcon, ListBulletIcon, CogIcon, } from '@heroicons/react/24/outline';
import Tooltip from 'components/v2/Tooltip';
import { Link } from 'react-router-dom';
import { useGroups } from 'hooks/unifiedview/useReports';
import { useTranslation } from 'react-i18next';
import { SIDEBAR_STATUSES, VIEWS } from './constants';
import { firstLetterTitlization } from '../../../utils/localization/utils';
import { usePageContext } from './PageContext';
import SessionStore from '../../../stores/SessionStore';
const containerClassName = 'fixed inset-y-0 lg:z-50 lg:flex lg:flex-col overflow-y-auto mt-16';
export default function Sidebar({ onToggle, activeTab, groupId, defaultState, }) {
    const { data } = useGroups();
    const { t } = useTranslation();
    const { updatePage } = usePageContext();
    const mappedGroups = useMemo(() => (data || [])
        .filter((group) => !group.parent)
        .map((group) => (Object.assign(Object.assign({}, group), { current: group._id === groupId, children: (data || [])
            .filter((subGroup) => subGroup.parent === group._id)
            .map((subGroup) => (Object.assign(Object.assign({}, subGroup), { current: subGroup._id === groupId }))) }))), [data, groupId]);
    const navigation = [
        {
            name: t(VIEWS.FAVORITES.slug),
            href: VIEWS.FAVORITES.href,
            icon: HeartIcon,
            current: activeTab === VIEWS.FAVORITES.name,
        },
        {
            name: t(VIEWS.ALL_REPORTS.slug),
            href: VIEWS.ALL_REPORTS.href,
            icon: ListBulletIcon,
            current: activeTab === VIEWS.ALL_REPORTS.name,
        },
        {
            name: t(VIEWS.MY_REPORTS.slug),
            href: VIEWS.MY_REPORTS.href,
            icon: UserIcon,
            current: activeTab === VIEWS.MY_REPORTS.name,
        },
        {
            name: t(VIEWS.EXPLORE.slug),
            href: VIEWS.EXPLORE.href,
            icon: MagnifyingGlassIcon,
            current: activeTab === VIEWS.EXPLORE.name,
        },
        // Only show Column Configuration for UV Report Managers
        ...(SessionStore.isUvReportManager()
            ? [
                {
                    name: t(VIEWS.COLUMN_CONFIG.slug),
                    href: VIEWS.COLUMN_CONFIG.href,
                    icon: CogIcon,
                    current: activeTab === VIEWS.COLUMN_CONFIG.name,
                },
            ]
            : []),
    ];
    const [hideNavText, setHideNavText] = useState(defaultState === SIDEBAR_STATUSES.COLLAPSED);
    function handleHideNavText() {
        setHideNavText(!hideNavText);
        if (onToggle) {
            onToggle(hideNavText ? SIDEBAR_STATUSES.EXPANDED : SIDEBAR_STATUSES.COLLAPSED);
        }
    }
    const getCurrentGroup = () => {
        let currentGroup = navigation.find((group) => group.current);
        if (!currentGroup) {
            currentGroup = mappedGroups.find((group) => group.current);
        }
        if (!currentGroup) {
            currentGroup = mappedGroups
                .map((group) => group.children)
                .flat()
                .find((group) => group.current);
        }
        return currentGroup || {};
    };
    useEffect(() => {
        const currentGroup = getCurrentGroup();
        if (currentGroup.name) {
            updatePage(currentGroup.href, currentGroup.name);
        }
    });
    const handleLinkClick = (href, name) => {
        updatePage(href, name);
    };
    function renderSubCategories(item, open) {
        return (React.createElement("div", { "data-testid": "div-504e4a9b" },
            React.createElement(Disclosure.Button, { "data-testid": "DisclosureButton-f7da49b1", key: `button-${item._id}`, className: classnames({
                    'bg-gray-50 text-green-600': item.current,
                    'text-gray-700 hover:text-green-600 hover:bg-gray-50': !item.current,
                }, 'group gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold flex items-center justify-between w-full'), "data-tip": item._id },
                React.createElement("div", { "data-testid": "div-8ac936e4", className: "gap-x-3 flex items-center" }, !hideNavText && (React.createElement(Link, { key: item._id, to: `/unifiedview/groups/${item._id}/reports`, className: classnames({
                        'bg-gray-50 text-green-600': item.current,
                        'text-gray-700 hover:text-green-600 hover:bg-gray-50': !item.current,
                    }), onClick: () => handleLinkClick(`/unifiedview/groups/${item._id}/reports`, item.name) },
                    React.createElement("span", { "data-testid": "span-163bb32f", className: "hidden lg:block" }, item.name)))),
                open ? (React.createElement(ChevronUpIcon, { className: "w-6 h-6 text-gray-500 hover:text-gray-700" })) : (React.createElement(ChevronRightIcon, { className: "w-6 h-6 text-gray-500 hover:text-gray-700" }))),
            React.createElement(Disclosure.Panel, { "data-testid": "DisclosurePanel-45985d40", key: `discloure-panel-${item._id}` }, item.children.map((subItem, index) => (React.createElement(Tooltip, { dataTestId: `Tooltip-824a5356-${index}`, id: subItem._id, key: `tooltip-${subItem._id}`, body: () => {
                    return (React.createElement(Link, { key: subItem._id, to: `/unifiedview/groups/${subItem._id}/reports`, className: classnames({
                            'bg-gray-50': subItem.current,
                            'text-green-600': subItem.current,
                            'text-gray-700 hover:text-green-600 hover:bg-gray-50': !subItem.current,
                        }, 'group flex gap-x-3 rounded-md pl-6 py-2 text-sm leading-6 font-semibold'), onClick: () => handleLinkClick(`/unifiedview/groups/${subItem._id}/reports`, subItem.name) },
                        React.createElement(ChartPieIcon, { className: classnames({
                                'text-green-600': subItem.current,
                                'text-gray-400 group-hover:text-green-600': !subItem.current,
                            }, 'h-6 w-6 shrink-0 sm:hidden'), "aria-hidden": "true" }),
                        !hideNavText && (React.createElement("span", { "data-testid": `span-2e525d71-${index}`, className: classnames('hidden lg:block') }, subItem.name))));
                } }, subItem.name))))));
    }
    function renderCategory(item, open) {
        if (open) {
            return (React.createElement("div", { "data-testid": "div-247445c8" }, renderSubCategories(item, open)));
        }
        return (React.createElement("div", { "data-testid": "div-b2de74a6" },
            React.createElement(Tooltip, { dataTestId: "Tooltip-0c20362d", id: item._id, key: `top-level-tooltip-${item._id}`, body: () => renderSubCategories(item, open) }, item.name)));
    }
    return (React.createElement("div", { "data-testid": "div-1e4b91b0", className: classnames(containerClassName, { 'lg:w-72': !hideNavText }) },
        React.createElement("div", { "data-testid": "div-945361c7", className: "h-full flex grow flex-col gap-y-5 overflow-y-auto border-r border-gray-200 bg-white px-6 pb-4 py-5 sm:py-6" },
            React.createElement("nav", { "data-testid": "nav-90ffb69a", className: "flex flex-1 flex-col" },
                React.createElement("div", { "data-testid": "div-cb5655aa", className: "flex justify-end" },
                    React.createElement("a", { "data-testid": "a-90d72f2c", href: "#", className: "text-gray-500 hover:text-green-600 hover:bg-gray-50 hidden lg:block", onClick: handleHideNavText },
                        hideNavText ? (React.createElement(ChevronDoubleRightIcon, { className: "h-6 w-6 shrink-0" })) : (React.createElement(ChevronDoubleLeftIcon, { className: "h-6 w-6 shrink-0" })),
                        React.createElement("span", { "data-testid": "span-b47cf579", className: "sr-only" }, hideNavText ? t('EXPAND') : t('COLLAPSE')))),
                React.createElement("ul", { "data-testid": "ul-d3d0cdcf", className: "flex flex-1 flex-col gap-y-7" },
                    React.createElement("li", { "data-testid": "li-7e441dae" },
                        React.createElement("ul", { "data-testid": "ul-746ac04d", className: "-mx-2 space-y-1" }, navigation.map((item, index) => (React.createElement("li", { "data-testid": `li-60f5d101-${index}`, key: item.name },
                            React.createElement(Tooltip, { dataTestId: `Tooltip-e207d312-${index}`, id: item._id, body: () => {
                                    return (React.createElement(Link, { to: item.href, className: classnames({
                                            'bg-gray-50 text-green-600': item.current,
                                            'text-gray-700 hover:text-green-600 hover:bg-gray-50': !item.current,
                                        }, 'group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold'), onClick: () => handleLinkClick(item.href, item.name) },
                                        React.createElement(item.icon, { "data-testid": `ItemIcon-1bdcca47-${index}`, className: classnames(item.current
                                                ? 'text-green-600'
                                                : 'text-gray-400 group-hover:text-green-600', 'h-6 w-6 shrink-0'), "aria-hidden": "true" }),
                                        !hideNavText && (React.createElement("span", { "data-testid": `span-1040d42c-${index}`, className: "hidden lg:block" }, item.name))));
                                } }, item.name)))))),
                    !hideNavText && (React.createElement("li", { "data-testid": "li-63d827f1", className: "hidden lg:block" },
                        React.createElement("div", { "data-testid": "div-459b2b0a", className: "text-xs font-semibold leading-6 text-gray-400" }, firstLetterTitlization(t('GROUP', { count: 2 }))),
                        React.createElement("ul", { "data-testid": "ul-3bcf9301", className: "-mx-2 mt-2 space-y-1" }, mappedGroups.map((item, index) => (React.createElement("li", { "data-testid": `li-e322ce68-${index}`, key: item._id }, (item.children || []).length > 0 ? (React.createElement(Disclosure, { key: `discloure-${item._id}`, defaultOpen: item.children.some((i) => i.current) }, ({ open }) => React.createElement(React.Fragment, null, renderCategory(item, open)))) : (React.createElement(Tooltip, { dataTestId: `Tooltip-87bc1cd3-${index}`, id: item._id, key: `top-level-tooltip-${item._id}`, body: () => (React.createElement(Link, { to: `/unifiedview/groups/${item._id}/reports`, className: classnames({
                                    'bg-gray-50 text-green-600': item.current,
                                    'text-gray-700 hover:text-green-600 hover:bg-gray-50': !item.current,
                                }, 'group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold'), onClick: () => handleLinkClick(`/unifiedview/groups/${item._id}/reports`, item.name) },
                                React.createElement(ChartPieIcon, { className: classnames({
                                        'text-green-600': item.current,
                                        'text-gray-400 group-hover:text-green-600': !item.current,
                                    }, 'h-6 w-6 shrink-0 sm:hidden'), "aria-hidden": "true" }),
                                !hideNavText && (React.createElement("span", { "data-testid": `span-e73a0536-${index}`, className: "hidden lg:block" }, item.name)))) }, item.name)))))))))))));
}
Sidebar.propTypes = {
    activeTab: PropTypes.string,
    groupId: PropTypes.string,
    defaultState: PropTypes.oneOf([
        SIDEBAR_STATUSES.EXPANDED,
        SIDEBAR_STATUSES.COLLAPSED,
    ]),
};
Sidebar.defaultProps = {
    activeTab: VIEWS.ALL_REPORTS.name,
    defaultState: SIDEBAR_STATUSES.EXPANDED,
};
